import { memo, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Plus, Check, ChevronDown } from "lucide-react";
import { Button } from "@/components/ui/button";
import { EnhancedBookCover } from "@/components/ui/enhanced-book-cover";
import { useEnhancedReadingList } from "@/hooks/use-enhanced-reading-list";
import { getAgeRating } from "@/lib/age-rating";
import type { Book } from "@/types/book";

interface BookHoverPreviewProps {
  book: Book;
  anchorRect: DOMRect;
  onClose: () => void;
  onOpenDialog: () => void;
  onPlay: () => void;
}

const SCALE_FACTOR = 1.4; // How much bigger than the original card

// Generate a "realistic" match percentage based on book id for consistency
const getMatchPercentage = (bookId?: number): number => {
  if (!bookId) return 85;
  // Use book id as seed for consistent but varied percentages
  const seed = bookId * 7919; // Prime number for better distribution
  return 75 + (seed % 23); // Range: 75-97%
};

export const BookHoverPreview = memo(function BookHoverPreview({
  book,
  anchorRect,
  onClose,
  onOpenDialog,
  onPlay,
}: BookHoverPreviewProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const { isBookInReadingList, toggleBookInReadingList } = useEnhancedReadingList();
  const [position, setPosition] = useState({ x: 0, y: 0, width: 0 });
  
  const isInList = book.id ? isBookInReadingList(book.id) : false;
  const ageRating = getAgeRating(book.tags);
  const matchPercentage = getMatchPercentage(book.id);
  
  // Calculate position to center over the thumbnail, scaled up
  useEffect(() => {
    const scaledWidth = anchorRect.width * SCALE_FACTOR;
    const scaledHeight = anchorRect.height * SCALE_FACTOR;
    const controlsHeight = 100; // Approximate height of controls section
    const totalHeight = scaledHeight + controlsHeight;
    const padding = 16;
    
    // Center horizontally on the anchor
    let x = anchorRect.left + (anchorRect.width / 2) - (scaledWidth / 2);
    // Center vertically on the anchor (card will extend below due to controls)
    let y = anchorRect.top - (anchorRect.height * (SCALE_FACTOR - 1) / 2);
    
    // Keep within viewport bounds - horizontal
    if (x < padding) x = padding;
    if (x + scaledWidth > window.innerWidth - padding) {
      x = window.innerWidth - scaledWidth - padding;
    }
    
    // Keep within viewport bounds - vertical
    if (y < padding) y = padding;
    if (y + totalHeight > window.innerHeight - padding) {
      y = window.innerHeight - totalHeight - padding;
    }
    
    setPosition({ x, y, width: scaledWidth });
  }, [anchorRect]);

  // Close on scroll
  useEffect(() => {
    const handleScroll = () => onClose();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [onClose]);

  const handleToggleList = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (book.id) {
      toggleBookInReadingList(book.id);
    }
  };

  const handlePlay = (e: React.MouseEvent) => {
    e.stopPropagation();
    onPlay();
  };

  const handleMoreInfo = (e: React.MouseEvent) => {
    e.stopPropagation();
    onOpenDialog();
  };

  // Extract first genre tag
  const genreTag = book.tags?.find(tag => 
    !['kids', 'teen', 'adult', 'mature'].includes(tag.toLowerCase())
  );

  return createPortal(
    <AnimatePresence>
      <motion.div
        ref={cardRef}
        initial={{ opacity: 0, scale: 0.85, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: 4 }}
        transition={{ 
          duration: 0.25, 
          ease: [0.4, 0, 0.2, 1],
          opacity: { duration: 0.2 }
        }}
        className="fixed z-[9999] bg-[#181818] rounded-md shadow-2xl overflow-hidden cursor-pointer border-2 border-white/20"
        style={{ 
          left: position.x, 
          top: position.y, 
          width: position.width,
          transformOrigin: 'center top'
        }}
        onMouseLeave={onClose}
        onClick={onOpenDialog}
      >
        {/* Cover Image - same aspect ratio as BookCard */}
        <div className="relative aspect-[2/3] w-full overflow-hidden rounded-t-md">
          <EnhancedBookCover
            src={book.cover_image_url || ''}
            alt={book.title}
            className="w-full h-full object-cover"
            bookId={book.id}
            priority
          />
        </div>

        {/* Controls Section */}
        <div className="p-3 bg-[#181818]">
          {/* Action Buttons Row */}
          <div className="flex items-center gap-2 mb-3">
            {/* Play Button - White filled */}
            <button
              onClick={handlePlay}
              className="w-9 h-9 rounded-full bg-white hover:bg-white/90 flex items-center justify-center transition-colors"
            >
              <Play className="w-4 h-4 text-black fill-black ml-0.5" />
            </button>
            
            {/* My List Button */}
            <button
              onClick={handleToggleList}
              className="w-9 h-9 rounded-full border-2 border-white/40 hover:border-white bg-transparent flex items-center justify-center transition-colors"
            >
              {isInList ? (
                <Check className="w-4 h-4 text-white" />
              ) : (
                <Plus className="w-4 h-4 text-white" />
              )}
            </button>
            
            {/* Spacer */}
            <div className="flex-1" />
            
            {/* More Info Button */}
            <button
              onClick={handleMoreInfo}
              className="w-9 h-9 rounded-full border-2 border-white/40 hover:border-white bg-transparent flex items-center justify-center transition-colors"
            >
              <ChevronDown className="w-4 h-4 text-white" />
            </button>
          </div>

          {/* Meta Row */}
          <div className="flex items-center gap-2 text-sm flex-wrap">
            <span className="text-green-400 font-semibold">
              {matchPercentage}% Match
            </span>
            {ageRating && (
              <span className="px-1.5 py-0.5 border border-white/30 text-white/70 text-xs">
                {ageRating}
              </span>
            )}
            {genreTag && (
              <span className="text-white/60 text-xs capitalize">
                {genreTag.replace(/-/g, ' ')}
              </span>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
});

